import React from "react";
import clsx from "clsx";

import { useUserContext } from "../../../contexts/userContext";

import { Reservation } from "../../../services/spaceReservationServices";

interface TimeTableProps {
  availableTimeStart?: string;
  availableTimeEnd?: string;
  size?: "small" | "medium" | "large";
  spaceReservationData?: Reservation[];
}

export default function TimeTable({
  availableTimeStart,
  availableTimeEnd,
  spaceReservationData,
  size = "medium",
}: TimeTableProps) {
  const [user] = useUserContext();

  const startHour = 0;
  const endHour = 23;
  const getColumnHeaders = (): JSX.Element[] => {
    const headers: JSX.Element[] = [];

    for (let hour = 0; hour <= 24; hour = hour + 2) {
      const columnHeader = hour.toString();
      headers.push(
        <div
          key={columnHeader}
          className="text-center w-[15px] text-[10px] font-medium leading-[14px]"
        >
          {columnHeader}
        </div>
      );
    }

    return headers;
  };

  const getCell = (
    key: string,
    reservation: Reservation | undefined
  ): JSX.Element => {
    //default

    let height = "";
    if (size === "large") height = "h-16";
    else if (size === "medium") height = "h-full";
    else height = "h-4";

    let color = "";
    let cellStyle = "";
    if (reservation !== undefined) {
      if (reservation.createdBy === user.memberId) {
        color += "bg-brand-primary-magenta border-white ";
      } else {
        color += "bg-brand-primary-gray-100 border-white ";
      }
    } else if (
      availableTimeStart &&
      availableTimeEnd &&
      (timeToMinutes(key) < timeToMinutes(availableTimeStart) ||
        timeToMinutes(key) >= timeToMinutes(availableTimeEnd))
    ) {
      cellStyle += "border-x-0 ";
      if (
        availableTimeEnd &&
        timeToMinutes(key) === timeToMinutes(availableTimeEnd)
      ) {
        color += "border-l border-l-neutral-10";
      }
    } else {
      color += "border-y-0 bg-brand-neutral-05 ";
      if (!key.endsWith("30")) {
        color += "border-l border-l-neutral-10";
      }
    }

    cellStyle += `${height} w-full ${color}`;

    // Check if cell is within the available time range

    return (
      <div
        key={key}
        className={clsx([
          "flex-1",
          {
            "bg-white":
              reservation &&
              timeToMinutes(key) >=
                timeToMinutes(reservation?.reservationTimeStart) &&
              timeToMinutes(key) + 30 <=
                timeToMinutes(reservation.reservationTimeEnd),
            "py-[3px] h-[30px]": reservation && size === "medium",
          },
        ])}
        data-testid={key}
      >
        <div
          className={clsx([
            cellStyle,
            {
              "rounded-l-[3px]":
                reservation &&
                size === "medium" &&
                timeToMinutes(reservation.reservationTimeStart) ===
                  timeToMinutes(key),
              "rounded-r-[3px]":
                reservation &&
                size === "medium" &&
                timeToMinutes(reservation.reservationTimeEnd) ===
                  timeToMinutes(key) + 30,
            },
          ])}
        ></div>
      </div>
    );
  };

  function timeToMinutes(time: string) {
    const [hours, mins] = time.split(":").map(Number);
    return hours * 60 + mins;
  }

  const getTableRows = (): JSX.Element => {
    const rows: JSX.Element[] = [];

    if (spaceReservationData === undefined) {
      return <div></div>;
    }

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour}:${minute}`;
        const currentTime = timeToMinutes(timeString);

        const reservation = spaceReservationData.find(
          (reservation: Reservation) => {
            const startTime = timeToMinutes(reservation.reservationTimeStart);
            const endTime = timeToMinutes(reservation.reservationTimeEnd);

            return startTime <= currentTime && currentTime < endTime;
          }
        );

        rows.push(getCell(timeString, reservation));
      }
    }

    return <div className="flex flex-row w-full">{rows}</div>;
  };

  const getTickCell = (
    key: string,
    reservation: Reservation | undefined
  ): JSX.Element => {
    //default

    let color = "";
    let cellStyle = "";
    color += "border-y-0 bg-white ";
    const [hour, minute] = key.split(":").map((string) => Number(string));
    if (hour !== 0 && minute !== 30) {
      color += "border-l border-brand-neutral-15";
    }

    cellStyle += `h-2.5 w-full ${color}`;

    // Check if cell is within the available time range

    return (
      <div
        key={key}
        className={clsx([
          "flex-1",
          {
            "bg-white":
              reservation &&
              timeToMinutes(key) >=
                timeToMinutes(reservation?.reservationTimeStart) &&
              timeToMinutes(key) + 30 <=
                timeToMinutes(reservation.reservationTimeEnd),
          },
        ])}
        data-testid={key}
      >
        <div
          className={clsx([
            cellStyle,
            {
              "border-opacity-60": hour % 2 === 0,
              "border-opacity-30": hour % 2 === 1,
            },
          ])}
        ></div>
      </div>
    );
  };

  const getTableTick = (): JSX.Element => {
    const rows: JSX.Element[] = [];

    if (spaceReservationData === undefined) {
      return <div></div>;
    }

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour}:${minute}`;
        const currentTime = timeToMinutes(timeString);

        const reservation = spaceReservationData.find(
          (reservation: Reservation) => {
            const startTime = timeToMinutes(reservation.reservationTimeStart);
            const endTime = timeToMinutes(reservation.reservationTimeEnd);

            return startTime <= currentTime && currentTime < endTime;
          }
        );

        rows.push(getTickCell(timeString, reservation));
      }
    }

    return <div className="flex flex-row w-full">{rows}</div>;
  };

  const rows = getTableRows();

  return (
    <div className="flex flex-col flex-grow mx-auto">
      <div
        className={clsx([
          "flex flex-col items-center",
          { "gap-[6.5px]": size === "small", "gap-1": size === "medium" },
        ])}
      >
        <div className="flex flex-row justify-between text-brand-neutral-30 text-sm w-full">
          {getColumnHeaders()}
        </div>
        <div className="w-full flex flex-col items-center">
          {size === "medium" && (
            <div
              className={clsx([
                {
                  "w-[calc(100%-13px)]": size === "medium",
                },
              ])}
            >
              {getTableTick()}
            </div>
          )}
          <div
            className={clsx([
              "border border-gray-300 rounded-[3px] bg-diagonal-time",
              {
                "w-[calc(100%-15px)]": size === "small",
                "w-[calc(100%-13px)]": size === "medium",
                "w-full": size !== "small" && size !== "medium",
              },
            ])}
          >
            {rows}
          </div>
        </div>
      </div>
    </div>
  );
}
